import { useAddRosterFreelancersMutation } from "@js/apps/rosters/api";
import { Snackbar } from "@js/components/snackbar";
import { pluralize } from "@js/utils/string";

type AddFreelancersToRostersArgs = {
  rosterIds: number[];
  freelancerIds: number[];
  note?: string;
  // per freelancer notes take precedence over the shared note
  notesByFreelancerId?: Record<number, string | undefined>;
};

// the endpoint accepts a single note per request, so freelancers sharing a note are sent together
const groupFreelancerIdsByNote = ({
  freelancerIds,
  note,
  notesByFreelancerId,
}: Omit<AddFreelancersToRostersArgs, "rosterIds">) => {
  const groups = new Map<string, number[]>();

  freelancerIds.forEach((freelancerId) => {
    const freelancerNote =
      notesByFreelancerId?.[freelancerId]?.trim() || note || "";
    groups.set(freelancerNote, [
      ...(groups.get(freelancerNote) ?? []),
      freelancerId,
    ]);
  });

  return [...groups.entries()];
};

export const useAddFreelancersToRosters = () => {
  const [addRosterFreelancers] = useAddRosterFreelancersMutation();

  // Resolves to true only when the freelancers were added to every roster.
  const addFreelancersToRosters = async ({
    rosterIds,
    freelancerIds,
    note,
    notesByFreelancerId,
  }: AddFreelancersToRostersArgs) => {
    if (!rosterIds.length || !freelancerIds.length) {
      return false;
    }

    const freelancerIdsByNote = groupFreelancerIdsByNote({
      freelancerIds,
      note,
      notesByFreelancerId,
    });

    const results = await Promise.allSettled(
      rosterIds.map((id) => {
        return Promise.all(
          freelancerIdsByNote.map(([groupNote, groupFreelancerIds]) => {
            return addRosterFreelancers({
              id,
              freelancer_ids: groupFreelancerIds,
              note: groupNote || undefined,
            }).unwrap();
          }),
        );
      }),
    );

    const failedCount = results.filter((result) => {
      return result.status === "rejected";
    }).length;

    if (failedCount) {
      Snackbar.error(
        `Added to ${rosterIds.length - failedCount} of ${rosterIds.length} roster${pluralize(rosterIds.length)}. ${failedCount} failed.`,
      );
      return false;
    }

    Snackbar.success(
      `Added ${freelancerIds.length} freelancer${pluralize(freelancerIds.length)} to ${rosterIds.length} roster${pluralize(rosterIds.length)}.`,
    );
    return true;
  };

  return { addFreelancersToRosters };
};
